// chapters/Chapter1/Chapter1Lesson2.js - Web version with complete mobile functionality
import React, { useState } from 'react';
import { ArrowLeft, Target, Check, X, RefreshCw, CheckCircle } from 'lucide-react';
import { Button, Card } from '../../components/ui';

const API_BASE_URL = 'https://growthsprouts-server.vercel.app';

export default function Chapter1Lesson2({ chapterId, lessonId, child, family, onNavigate }) {
  const currentChapterId = chapterId || '1';
  const [currentSection, setCurrentSection] = useState(0);
  const [completedActivities, setCompletedActivities] = useState({});
  const [selectedElements, setSelectedElements] = useState({});
  const [currentGoalScenario, setCurrentGoalScenario] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Debug logging
  console.log('🎯 Chapter1Lesson2 loaded with props:', { 
    chapterId, 
    lessonId, 
    child: child?.id, 
    family: family?.id 
  });

  const sections = [
    {
      type: "intro",
      title: "The Power of SMART Goals",
      content: {
        subtitle: "Building Goals That Actually Work",
        text: "Imagine trying to build a house without blueprints. That's what achieving financial success is like without SMART goals. Today, you'll learn to construct goals that guide you to success.",
        icon: "🎯"
      }
    },
    {
      type: "story-interactive",
      title: "Meet Goal Builder Maya",
      content: {
        panels: [
          {
            scene: "Maya at her desk with scattered papers",
            text: "Maya wanted to 'save money' but kept failing...",
            icon: "😕",
            emotion: "frustrated"
          },
          {
            scene: "Maya learning about SMART goals",
            text: "Then she discovered the SMART goal framework!",
            icon: "💡",
            emotion: "curious"
          },
          {
            scene: "Maya writing specific goals",
            text: "She transformed 'save money' into 'Save $1,200 for emergency fund by December 31st'",
            icon: "✏️",
            emotion: "focused"
          },
          {
            scene: "Maya celebrating success",
            text: "6 months later, Maya achieved her goal!",
            icon: "🎉",
            emotion: "successful"
          }
        ]
      }
    },
    {
      type: "smart-builder",
      title: "SMART Goal Construction Workshop",
      content: {
        instruction: "Build a SMART goal by selecting the right elements for each component",
        scenarios: [
          {
            id: 1,
            weakGoal: "I want to save money for vacation",
            components: {
              S: {
                question: "Make it Specific - What exactly do you want to save for?",
                options: [
                  { text: "Save money", correct: false },
                  { text: "Save for vacation", correct: false },
                  { text: "Save $2,500 for a 7-day trip to Japan", correct: true }
                ]
              },
              M: {
                question: "Make it Measurable - How will you track progress?",
                options: [
                  { text: "Save when I can", correct: false },
                  { text: "Save $2,500 total", correct: true },
                  { text: "Save some money each month", correct: false }
                ]
              },
              A: {
                question: "Make it Achievable - Is this realistic for your income?",
                options: [
                  { text: "Save $5,000 in 2 months", correct: false },
                  { text: "Save $2,500 in 10 months ($250/month)", correct: true },
                  { text: "Save $2,500 somehow", correct: false }
                ]
              },
              R: {
                question: "Make it Relevant - Why does this matter to you?",
                options: [
                  { text: "Because travel is trendy", correct: false },
                  { text: "To experience Japanese culture and create memories", correct: true },
                  { text: "Everyone else is traveling", correct: false }
                ]
              },
              T: {
                question: "Make it Time-bound - When will you achieve this?",
                options: [
                  { text: "Someday", correct: false },
                  { text: "By October 15, 2024", correct: true },
                  { text: "When I have enough", correct: false }
                ]
              }
            }
          },
          {
            id: 2,
            weakGoal: "I need to pay off debt",
            components: {
              S: {
                question: "Make it Specific - What debt exactly?",
                options: [
                  { text: "Pay off all debt", correct: false },
                  { text: "Pay off $3,200 credit card debt", correct: true },
                  { text: "Get rid of debt", correct: false }
                ]
              },
              M: {
                question: "Make it Measurable - How much total?",
                options: [
                  { text: "Pay off as much as possible", correct: false },
                  { text: "Reduce debt significantly", correct: false },
                  { text: "Pay off exactly $3,200", correct: true }
                ]
              },
              A: {
                question: "Make it Achievable - What's realistic monthly?",
                options: [
                  { text: "Pay $1,600 per month", correct: false },
                  { text: "Pay $400 per month for 8 months", correct: true },
                  { text: "Pay everything immediately", correct: false }
                ]
              },
              R: {
                question: "Make it Relevant - Why does this matter to you?",
                options: [
                  { text: "To reduce financial stress and improve credit score", correct: true },
                  { text: "Because debt is bad", correct: false },
                  { text: "My friend told me to", correct: false }
                ]
              },
              T: {
                question: "Make it Time-bound - When will you be debt-free?",
                options: [
                  { text: "Eventually", correct: false },
                  { text: "By March 31, 2025", correct: true },
                  { text: "As soon as possible", correct: false }
                ]
              }
            }
          },
          {
            id: 3,
            weakGoal: "I want to learn about finance",
            components: {
              S: {
                question: "Make it Specific - What exactly will you learn?",
                options: [
                  { text: "Learn about money", correct: false },
                  { text: "Complete financial literacy course and read 3 finance books", correct: true },
                  { text: "Get better with finances", correct: false }
                ]
              },
              M: {
                question: "Make it Measurable - How will you track completion?",
                options: [
                  { text: "Learn some things", correct: false },
                  { text: "Complete 1 course + read 3 books = 4 total items", correct: true },
                  { text: "Learn until I feel ready", correct: false }
                ]
              },
              A: {
                question: "Make it Achievable - Can you realistically do this?",
                options: [
                  { text: "Read 20 books in 1 month", correct: false },
                  { text: "1 course (2 hours/week) + 1 book per month", correct: true },
                  { text: "Learn everything about finance", correct: false }
                ]
              },
              R: {
                question: "Make it Relevant - Why does this matter to you?",
                options: [
                  { text: "It sounds important", correct: false },
                  { text: "To make better financial decisions and secure my future", correct: true },
                  { text: "Someone said I should", correct: false }
                ]
              },
              T: {
                question: "Make it Time-bound - Complete by when?",
                options: [
                  { text: "Someday", correct: false },
                  { text: "Complete course by June 30, finish all books by September 30", correct: true },
                  { text: "When I finish", correct: false }
                ]
              }
            }
          }
        ]
      }
    },
    {
      type: "reflection",
      title: "Your SMART Goal Mastery",
      content: {
        prompt: "How will you apply SMART goals to your financial journey?",
        reflection: "Remember: SMART goals are your blueprint for financial success. Every major financial achievement starts with a well-constructed goal!"
      }
    }
  ];

  const currentScenario = sections[2]?.content?.scenarios[currentGoalScenario];
  const smartComponents = ['S', 'M', 'A', 'R', 'T'];

  // Handle SMART component selection
  const handleElementSelect = (component, optionIndex) => {
    const key = `${currentSection}-${currentGoalScenario}-${component}`;
    setSelectedElements({
      ...selectedElements,
      [key]: optionIndex
    });

    // Check if all components are selected WITH CORRECT ANSWERS
    const updatedElements = { ...selectedElements, [key]: optionIndex };
    const allCorrectlySelected = smartComponents.every(comp => {
      const selectedKey = `${currentSection}-${currentGoalScenario}-${comp}`;
      const selectedIndex = updatedElements[selectedKey];
      if (selectedIndex === undefined) return false;
      
      const componentData = currentScenario.components[comp];
      const selectedOption = componentData.options[selectedIndex];
      return selectedOption && selectedOption.correct;
    });

    if (allCorrectlySelected) {
      setCompletedActivities({
        ...completedActivities,
        [currentSection]: true
      });
    } else {
      // Remove completion if not all are correct
      const newCompleted = { ...completedActivities };
      delete newCompleted[currentSection];
      setCompletedActivities(newCompleted);
    }
  };

  // Complete lesson function (API call)
  const completeLesson = async () => {
    try {
      console.log('🎯 Starting lesson completion...', {
        childId: child?.id,
        familyId: family?.id,
        chapterId: currentChapterId,
        lessonId: 2
      });

      if (!child || !family) {
        console.error('❌ Missing child or family data');
        alert('Error: Missing user data');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/lessons/complete`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({
          child_id: child.id,
          family_id: family.id,
          chapter_id: parseInt(currentChapterId),
          lesson_id: 2
        })
      });

      console.log('🌐 API response status:', response.status);
      
      const result = await response.json();
      console.log('📊 API response data:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save progress');
      }
      
      console.log('✅ Lesson completed successfully!');
      return result;
      
    } catch (error) {
      console.error('❌ Error completing lesson:', error);
      alert('Failed to save progress. Please try again.');
      throw error;
    }
  };

  // Navigation functions
  const handleBackToChapter = () => {
    if (onNavigate) {
      onNavigate('financialLiteracyChapter', { chapterId: currentChapterId });
    }
  };

  const handleCompleteLesson = async () => {
    try {
      console.log('🚀 Completing lesson...');
      await completeLesson();
      console.log('🏁 Lesson completion saved!');
      
      // Show success message
      setShowSuccessMessage(true);
      
      // Navigate back after showing message
      setTimeout(() => {
        handleBackToChapter();
      }, 2000);
      
    } catch (error) {
      console.error('❌ Failed to complete lesson:', error);
      // Don't navigate if completion failed
    }
  };

  const isLastSection = currentSection === sections.length - 1;
  
  // Check if user can proceed to next section
  const canProceed = () => {
    if (sections[currentSection].type === "intro" || sections[currentSection].type === "story-interactive" || sections[currentSection].type === "reflection") {
      return true;
    }
    if (sections[currentSection].type === "smart-builder") {
      // For smart-builder, require all components to be selected correctly
      const allCorrectlySelected = smartComponents.every(comp => {
        const selectedKey = `${currentSection}-${currentGoalScenario}-${comp}`;
        const selectedIndex = selectedElements[selectedKey];
        if (selectedIndex === undefined) return false;
        
        const componentData = currentScenario.components[comp];
        const selectedOption = componentData.options[selectedIndex];
        return selectedOption && selectedOption.correct;
      });
      
      return allCorrectlySelected;
    }
    return completedActivities[currentSection];
  };

  // Reset scenario function
  const resetScenario = () => {
    setSelectedElements({});
    const newCompleted = { ...completedActivities };
    delete newCompleted[currentSection];
    setCompletedActivities(newCompleted);
  };

  // Next scenario function
  const nextScenario = () => {
    if (currentGoalScenario < sections[2].content.scenarios.length - 1) {
      setCurrentGoalScenario(currentGoalScenario + 1);
      resetScenario();
    }
  };

  // Render different section types
  const renderSection = () => {
    const section = sections[currentSection];

    switch (section.type) {
      case "intro":
        return (
          <div className="text-center space-y-6 max-w-3xl mx-auto">
            <div className="text-6xl mb-4">{section.content.icon}</div>
            <h2 className="text-3xl font-bold text-blue-800 mb-4">{section.content.subtitle}</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              {section.content.text}
            </p>
          </div>
        );

      case "story-interactive":
        return (
          <div className="space-y-8 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-800 text-center mb-8">
              Maya's SMART Goal Journey
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {section.content.panels.map((panel, idx) => (
                <Card key={idx} className={`p-6 border-2 ${
                  panel.emotion === 'frustrated' 
                    ? 'border-red-200 bg-red-50' 
                    : panel.emotion === 'curious'
                    ? 'border-yellow-200 bg-yellow-50'
                    : panel.emotion === 'focused'
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-green-200 bg-green-50'
                }`}>
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="text-4xl">{panel.icon}</span>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">Step {idx + 1}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        panel.emotion === 'frustrated' 
                          ? "bg-red-100 text-red-800" 
                          : panel.emotion === 'curious'
                          ? "bg-yellow-100 text-yellow-800"
                          : panel.emotion === 'focused'
                          ? "bg-blue-100 text-blue-800"
                          : "bg-green-100 text-green-800"
                      }`}>
                        {panel.emotion}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-700">{panel.text}</p>
                </Card>
              ))}
            </div>
          </div>
        );

      case "smart-builder":
        return (
          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="text-center">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-4">SMART Goal Workshop</h2>
              <p className="text-gray-600 mb-4">{section.content.instruction}</p>
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 rounded-lg px-4 py-2">
                <span className="text-sm font-medium">Scenario {currentGoalScenario + 1} of {sections[2].content.scenarios.length}:</span>
                <span className="text-sm italic">"{currentScenario.weakGoal}"</span>
              </div>
            </div>

            {/* SMART Components Grid */}
            <div className="grid gap-6">
              {smartComponents.map((component, idx) => {
                const componentData = currentScenario.components[component];
                const selectedKey = `${currentSection}-${currentGoalScenario}-${component}`;
                const selectedIndex = selectedElements[selectedKey];
                
                // Rainbow colors for each SMART component
                const componentColors = [
                  'from-pink-500 to-rose-500',     // S - Pink
                  'from-purple-500 to-violet-500', // M - Purple  
                  'from-blue-500 to-cyan-500',     // A - Blue
                  'from-green-500 to-emerald-500', // R - Green
                  'from-yellow-500 to-orange-500'  // T - Yellow/Orange
                ];
                
                return (
                  <Card key={component} className="p-6 border-2 border-gray-100 hover:border-gray-200 transition-all">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold bg-gradient-to-r ${componentColors[idx]} ${
                        selectedIndex !== undefined && componentData.options[selectedIndex]?.correct
                          ? 'ring-4 ring-green-200'
                          : selectedIndex !== undefined
                          ? 'ring-4 ring-red-200'
                          : ''
                      }`}>
                        {component}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {componentData.question}
                      </h3>
                    </div>
                    
                    <div className="grid gap-3">
                      {componentData.options.map((option, optionIndex) => (
                        <button
                          key={optionIndex}
                          onClick={() => handleElementSelect(component, optionIndex)}
                          className={`p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                            selectedIndex === optionIndex
                              ? option.correct
                                ? "border-green-400 bg-gradient-to-r from-green-50 to-emerald-50"
                                : "border-red-400 bg-gradient-to-r from-red-50 to-pink-50"
                              : "border-gray-200 hover:border-gray-300 bg-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-gray-800">{option.text}</span>
                            {selectedIndex === optionIndex && (
                              <div className="flex items-center">
                                {option.correct ? (
                                  <Check className="w-5 h-5 text-green-600" />
                                ) : (
                                  <X className="w-5 h-5 text-red-600" />
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Progress and Actions */}
            {completedActivities[currentSection] && (
              <Card className="p-6 border-2 border-green-300 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <div>
                      <h3 className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">SMART Goal Complete!</h3>
                      <p className="text-green-700">You've successfully built a SMART goal!</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={resetScenario}
                      className="flex items-center gap-2 border-purple-300 text-purple-600 hover:bg-purple-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Try Again
                    </Button>
                    {currentGoalScenario < sections[2].content.scenarios.length - 1 && (
                      <Button
                        onClick={nextScenario}
                        className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                      >
                        Next Scenario
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </div>
        );

      case "reflection":
        return (
          <div className="text-center space-y-6 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-800">{section.title}</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <p className="text-lg text-blue-800 font-medium mb-4">{section.content.prompt}</p>
              <p className="text-gray-700 leading-relaxed">{section.content.reflection}</p>
            </div>
            <div className="text-4xl">🎯</div>
          </div>
        );

      default:
        return <div>Unknown section type</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-purple-50">
      {/* Success Message Overlay */}
      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-xl">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-blue-800 mb-2">Congratulations!</h2>
            <p className="text-gray-600 mb-4">
              You've mastered "The Power of SMART Goals"
            </p>
            <div className="text-blue-600 font-medium">
              Returning to chapter overview...
            </div>
            <div className="mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={handleBackToChapter}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Chapter
          </Button>
          <div className="text-sm text-gray-500">
            Section {currentSection + 1} of {sections.length}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Lesson Progress</span>
            <span>{Math.round(((currentSection + 1) / sections.length) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Lesson Title */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Target className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-800">The Power of SMART Goals</h1>
          </div>
          <p className="text-lg text-gray-600">Transform vague wishes into achievable financial goals</p>
        </div>

        {/* Section Content */}
        <div className="mb-12">
          {renderSection()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {sections.map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full ${
                  index === currentSection
                    ? 'bg-blue-500'
                    : index < currentSection
                    ? 'bg-blue-300'
                    : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {isLastSection ? (
            <Button
              onClick={handleCompleteLesson}
              disabled={!canProceed()}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Complete Lesson
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentSection(currentSection + 1)}
              disabled={!canProceed()}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              Next
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Button>
          )}
        </div>

        {/* Completion Status */}
        {!canProceed() && sections[currentSection].type === "smart-builder" && (
          <div className="mt-4 text-center">
            <p className="text-amber-600 text-sm">
              Select the correct answer for all SMART components to continue
            </p>
          </div>
        )}
      </div>
    </div>
  );
}